import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "~/api";
import { SpinnerIcon } from "~/components/icon";
import { useKeybindings } from "~/hooks/useKeyBindings";
import type { OrgSearchResult } from "~/types";
import { cn, formatBytes, formatNumber, stripTagPrefix } from "~/utils";

/** Org name prefixed with its customer. */
export function OrgName({
  org,
}: {
  org: Pick<OrgSearchResult, "name" | "customer_name">;
}) {
  return (
    <span className="font-medium">
      {org.customer_name ? (
        <>
          <span className="text-gray-500">{org.customer_name}</span>
          <span className="text-gray-400"> → </span>
        </>
      ) : null}
      {org.name}
    </span>
  );
}

interface OrgSearchModalProps {
  onSuccess?: () => void;
}

export function OrgSearchModal({ onSuccess }: OrgSearchModalProps) {
  const navigate = useNavigate();
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState<OrgSearchResult[]>(
    [],
  );
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [selectedOrgIndex, setSelectedOrgIndex] = useState(0);
  const orgSearchInputRef = useRef<HTMLInputElement>(null);
  const orgResultsRef = useRef<HTMLDivElement>(null);

  const normalizedQuery = useMemo(
    () => stripTagPrefix(orgSearchQuery),
    [orgSearchQuery],
  );

  useEffect(() => {
    // Don't list arbitrary orgs before the user has typed anything selective.
    if (normalizedQuery.length < 2) {
      setOrgSearchResults([]);
      setOrgSearchLoading(false);
      return;
    }

    // An in-flight request outlives its query, and would otherwise repopulate
    // results for a query that's since been cleared or typed past.
    let superseded = false;

    const fetchOrgs = async () => {
      setOrgSearchLoading(true);
      try {
        const results = await api.searchOrgs(normalizedQuery);
        if (superseded) return;
        setOrgSearchResults(results);
        setSelectedOrgIndex(0);
      } catch (error) {
        if (superseded) return;
        console.error("Failed to search orgs:", error);
        setOrgSearchResults([]);
      } finally {
        if (!superseded) setOrgSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchOrgs, 150);
    return () => {
      superseded = true;
      clearTimeout(debounceTimer);
    };
  }, [normalizedQuery]);

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => {
      orgSearchInputRef.current?.focus();
    }, 50);
  }, []);

  // Keep the highlighted row in view as arrow keys move past the visible edge.
  useEffect(() => {
    orgResultsRef.current
      ?.querySelector(`[data-index="${selectedOrgIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedOrgIndex]);

  const handleSelectOrg = useCallback(
    (org: OrgSearchResult) => {
      onSuccess?.();
      navigate(`/orgs/${org.id}`);
    },
    [onSuccess, navigate],
  );

  const emptyStateMessage = useMemo(() => {
    if (normalizedQuery.length === 0) return "Type to search...";
    if (normalizedQuery.length < 2) return "Type at least 2 characters...";
    return "No organizations found";
  }, [normalizedQuery]);

  // Navigation keybindings
  useKeybindings(
    {
      ArrowDown: (e: KeyboardEvent) => {
        e.preventDefault();
        setSelectedOrgIndex((prev) =>
          Math.min(prev + 1, orgSearchResults.length - 1),
        );
      },
      ArrowUp: (e: KeyboardEvent) => {
        e.preventDefault();
        setSelectedOrgIndex((prev) => Math.max(prev - 1, 0));
      },
      Enter: (e: KeyboardEvent) => {
        e.preventDefault();
        if (orgSearchResults[selectedOrgIndex]) {
          handleSelectOrg(orgSearchResults[selectedOrgIndex]);
        }
      },
    },
    {
      allowedElements: ["INPUT", "BUTTON", "DIV", "BODY"],
      disallowedElements: [],
      enabled: true,
    },
  );

  return (
    <div className="space-y-3">
      <div className="relative">
        <input
          ref={orgSearchInputRef}
          type="text"
          value={orgSearchQuery}
          onChange={(e) => setOrgSearchQuery(e.target.value)}
          placeholder="Search by org name, org ID, WorkOS ID, or customer..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          autoComplete="off"
        />
        {orgSearchLoading ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <SpinnerIcon className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        ) : null}
      </div>

      <div
        ref={orgResultsRef}
        className="h-64 overflow-y-auto rounded-md border border-gray-200"
      >
        {orgSearchLoading ? (
          [...Array(6)].map((_, i) => (
            <div
              key={i}
              className="flex w-full animate-pulse items-center justify-between px-3 py-2"
            >
              <div className="flex flex-col gap-1.5">
                <div className="h-4 w-32 rounded bg-gray-200" />
                <div className="h-3 w-48 rounded bg-gray-100" />
                <div className="h-3 w-40 rounded bg-gray-100" />
              </div>
              <div className="h-5 w-12 rounded-full bg-gray-200" />
            </div>
          ))
        ) : orgSearchResults.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {emptyStateMessage}
          </div>
        ) : null}
        {orgSearchResults.map((org, index) => (
          <button
            key={org.id}
            data-index={index}
            onClick={() => handleSelectOrg(org)}
            onMouseEnter={() => setSelectedOrgIndex(index)}
            className={cn(
              "flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors",
              index === selectedOrgIndex
                ? "bg-blue-50 text-blue-900"
                : "hover:bg-gray-50",
            )}
          >
            <div className="flex flex-col gap-0.5">
              <OrgName org={org} />
              <div>
                <span className="font-mono text-xs text-gray-500">
                  {org.id}
                </span>
                <span className="text-gray-500"> | </span>
                <span className="font-mono text-xs text-gray-500">
                  {org.workos_id}
                </span>
              </div>
              {/* Omitted, not zeroed: no snapshot doesn't mean no data. */}
              {org.stats ? (
                <div className="text-[11px] leading-tight text-gray-400">
                  {formatNumber(org.stats.namespace_count)} ns ·{" "}
                  {formatNumber(org.stats.total_vectors)} vectors ·{" "}
                  {formatBytes(org.stats.total_bytes)}
                </div>
              ) : null}
            </div>
            <span
              className={cn(
                "ml-3 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                org.is_byoc
                  ? "bg-blue-100 text-blue-700"
                  : "bg-green-100 text-green-700",
              )}
            >
              {org.is_byoc ? "BYOC" : "SaaS"}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Up/Down</kbd>
          <span>navigate</span>
        </div>
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Enter</kbd>
          <span>select</span>
        </div>
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Esc</kbd>
          <span>close</span>
        </div>
      </div>
    </div>
  );
}
